import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { injectService } from '@patsnap/tahiti-core';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import type { NoveltySearchApiEnvelope } from '../novelty-search-api-response';
import type {
    NoveltySearchSseEvent,
    NoveltySearchSseEventPayload,
    NoveltySearchApi,
    CheckTelecomDomainRequest,
    CheckTelecomDomainResponse,
    ExtractInventionsRequest,
    ExtractInventionsResponse,
    ExtractInventionsSseMessage,
    AddInventionRequest,
    AddInventionResponse,
    ExtractClaimRequest,
    ExtractClaimResponse,
    ExtractClaimSseMessage,
    ExtractFeaturesRequest,
    ExtractFeaturesResponse,
    ExtractFeaturesSseMessage,
    PredictWorkingGroupRequest,
    PredictWorkingGroupResponse,
    ExtractSearchElementsRequest,
    ExtractSearchElementsResponse,
    PredictWgAndKeywordsRequest,
    PredictWgAndKeywordsResponse,
    SearchTdocsRequest,
    SearchTdocsResponse,
    NoveltySearchCompareRequest,
    NoveltySearchCompareResponse,
    NoveltySearchReportRequest,
    NoveltySearchReportResponse,
    NoveltySearchExportRequest,
    NoveltySearchExportTelecomRequest,
    NoveltySearchTaskHistoryParams,
    NoveltySearchTaskHistoryResponse,
} from '../novelty-search-api';
import { NoveltySearchHttpService } from '../../services/novelty-search-http-service';
import { resolveNoveltySearchApiPath } from '@/utils/api-path-resolver';

@injectable()
export class NoveltySearchApiImpl extends NoveltySearchHttpService implements NoveltySearchApi {
    private buildNoveltySseUrl(endpoint: string): Promise<string> {
        return resolveNoveltySearchApiPath(endpoint).then((resolvedPath) => {
            const baseUrl = import.meta.env.DEV ? '' : (import.meta.env.VITE_TELECOM_API || '');
            return `${baseUrl}${resolvedPath}`;
        });
    }

    private getNoveltySseClient<T>(url: string, body: unknown) {
        const sseClient = injectService<FactoryPsHttpSSE>(FactoryPsHttpSSE);
        return sseClient<T>()
            .init({
                url,
                data: (body ?? {}) as Record<string, unknown>,
                hasFinalMessage: () => true,
            })
            .setFetchHeaders({
                ...this.getDefaultHeaders(),
                'Content-Type': 'application/json',
            });
    }

    private async runNoveltySse<TData, TResp extends NoveltySearchApiEnvelope<TData>>(
        endpoint: string,
        body: unknown,
        noFinishErrorMessage: string,
        apiErrorMessage: string
    ): Promise<TResp> {
        const url = await this.buildNoveltySseUrl(endpoint);
        const stream = this.getNoveltySseClient<NoveltySearchSseEvent<TData>>(url, body);

        return new Promise<TResp>((resolve, reject) => {
            let finalResult = {
                status: false,
                error_code: -1,
                error_msg: noFinishErrorMessage,
            } as TResp;

            stream
                .onMessage((event) => {
                    const raw = (event as { data?: unknown })?.data;
                    if (raw == null) return;

                    const envelope = raw as NoveltySearchSseEvent<TData>;
                    const payload = (
                        envelope && typeof envelope === 'object' && 'data' in envelope
                            ? envelope.data
                            : raw
                    ) as NoveltySearchSseEventPayload<TData> | undefined;

                    if (envelope && typeof envelope === 'object' && 'error_code' in envelope) {
                        if (envelope.status !== true || envelope.error_code !== 0) {
                            finalResult = {
                                status: false,
                                error_code: envelope.error_code ?? -1,
                                error_msg: envelope.error_msg || apiErrorMessage,
                            } as TResp;
                            return;
                        }
                    }

                    if (import.meta.env.DEV) {
                        console.debug(`[NoveltySearchApi] ${endpoint} event:`, payload?.status);
                    }

                    if (payload?.status === 'FINISH') {
                        finalResult = {
                            status: true,
                            error_code: 0,
                            error_msg: '',
                            data: payload.content ?? ({} as TData),
                        } as TResp;
                    }
                })
                .onError((err) => reject(err))
                .onFinish(() => resolve(finalResult))
                .start()
                .catch((err) => reject(err));
        });
    }

    checkTelecomDomain(body: CheckTelecomDomainRequest): Promise<CheckTelecomDomainResponse> {
        return this.postNoveltyApi<CheckTelecomDomainResponse>('task/init', body);
    }

    extractInventions(body: ExtractInventionsRequest): Promise<ExtractInventionsResponse> {
        return this.postNoveltyApi<ExtractInventionsResponse>('extract_inventions', body);
    }

    async extractInventionsSse(body: ExtractInventionsRequest): Promise<ExtractInventionsResponse> {
        return this.runNoveltySse<ExtractInventionsResponse['data'], ExtractInventionsResponse>(
            'extract_inventions_sse',
            body,
            'extract_inventions_sse did not receive FINISH event',
            'extract_inventions_sse failed'
        );
    }

    async extractInventionsStream(body: ExtractInventionsRequest): Promise<PsHttpSSE<ExtractInventionsSseMessage>> {
        const url = await this.buildNoveltySseUrl('extract_inventions_sse');
        return this.getNoveltySseClient<ExtractInventionsSseMessage>(url, body);
    }

    addInvention(body: AddInventionRequest): Promise<AddInventionResponse> {
        return this.postNoveltyApi<AddInventionResponse>('check_invention', body);
    }

    async extractClaimSse(body: ExtractClaimRequest): Promise<ExtractClaimResponse> {
        return this.runNoveltySse<ExtractClaimResponse['data'], ExtractClaimResponse>(
            'extract_claim_sse',
            body,
            'extract_claim_sse did not receive FINISH event',
            'extract_claim_sse failed'
        );
    }

    async extractClaimStream(body: ExtractClaimRequest): Promise<PsHttpSSE<ExtractClaimSseMessage>> {
        const url = await this.buildNoveltySseUrl('extract_claim_sse');
        return this.getNoveltySseClient<ExtractClaimSseMessage>(url, body);
    }

    predictWorkingGroup(body: PredictWorkingGroupRequest): Promise<PredictWorkingGroupResponse> {
        return this.postNoveltyApi<PredictWorkingGroupResponse>('predict_working_group', body);
    }

    extractSearchElements(body: ExtractSearchElementsRequest): Promise<ExtractSearchElementsResponse> {
        return this.postNoveltyApi<ExtractSearchElementsResponse>('extract_search_elements', body);
    }

    async extractFeaturesSse(body: ExtractFeaturesRequest): Promise<ExtractFeaturesResponse> {
        return this.runNoveltySse<ExtractFeaturesResponse['data'], ExtractFeaturesResponse>(
            'extract_features_sse',
            body,
            'extract_features_sse did not receive FINISH event',
            'extract_features_sse failed'
        );
    }

    async extractFeaturesStream(body: ExtractFeaturesRequest): Promise<PsHttpSSE<ExtractFeaturesSseMessage>> {
        const url = await this.buildNoveltySseUrl('extract_features_sse');
        return this.getNoveltySseClient<ExtractFeaturesSseMessage>(url, body);
    }

    predictWgAndKeywords(body: PredictWgAndKeywordsRequest): Promise<PredictWgAndKeywordsResponse> {
        return this.postNoveltyApi<PredictWgAndKeywordsResponse>('predict_wg_and_keywords', body);
    }

    searchTdocs(body: SearchTdocsRequest): Promise<SearchTdocsResponse> {
        return this.postNoveltyApi<SearchTdocsResponse>('search_tdocs', body);
    }

    async searchTdocsSse(body: SearchTdocsRequest): Promise<SearchTdocsResponse> {
        return this.runNoveltySse<SearchTdocsResponse['data'], SearchTdocsResponse>(
            'search_tdocs_sse',
            body,
            'search_tdocs_sse did not receive FINISH event',
            'search_tdocs_sse failed'
        );
    }

    compare(body: NoveltySearchCompareRequest): Promise<NoveltySearchCompareResponse> {
        return this.postNoveltyApi<NoveltySearchCompareResponse>('compare', body);
    }

    async compareSse(body: NoveltySearchCompareRequest): Promise<NoveltySearchCompareResponse> {
        return this.runNoveltySse<NoveltySearchCompareResponse['data'], NoveltySearchCompareResponse>(
            'compare_sse',
            body,
            'compare_sse did not receive FINISH event',
            'compare_sse failed'
        );
    }

    report(body: NoveltySearchReportRequest): Promise<NoveltySearchReportResponse> {
        return this.postNoveltyApi<NoveltySearchReportResponse>('report', body);
    }

    async reportSse(body: NoveltySearchReportRequest): Promise<NoveltySearchReportResponse> {
        return this.runNoveltySse<NoveltySearchReportResponse['data'], NoveltySearchReportResponse>(
            'report_sse',
            body,
            'report_sse did not receive FINISH event',
            'report_sse failed'
        );
    }

    exportReport(body: NoveltySearchExportRequest): Promise<Blob> {
        return this.postNoveltyApiBlob('export', body);
    }

    exportTelecomReport(body: NoveltySearchExportTelecomRequest): Promise<Blob> {
        return this.postNoveltyApiBlob('export_telecom', body);
    }

    getTaskHistory(params: NoveltySearchTaskHistoryParams): Promise<NoveltySearchTaskHistoryResponse> {
        return this.getNoveltyApi<NoveltySearchTaskHistoryResponse>('task/history', {
            task_id: params.task_id,
        });
    }
}
